import { Button, Input, Text, Textarea, toast } from "@medusajs/ui"
import { useState } from "react"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { Field } from "@/components/form"
import { DataTable, EmptyState, ErrorState, PageHeader, SearchInput, Section, Status, usePage } from "@/components/ui"
import { formatDate, formatMoney } from "@/lib/format"
import { useIssueGiftCard, useGiftCard, useGiftCards, useUpdateGiftCard, type GiftCard } from "@/lib/gift-cards"
import { useCan } from "@/lib/permissions"
import { errorMessage } from "@/lib/sdk"

const PAGE = 50
const STATUSES = [["", "All"], ["active", "Active"], ["depleted", "Depleted"], ["expired", "Expired"], ["disabled", "Disabled"]] as const
const card = (c: GiftCard) => `•••• ${c.last4}`
const dateOnly = (iso: string | null) => (iso ? iso.slice(0, 10) : "")
const toIso = (d: string) => (d ? new Date(d + "T23:59:59.000Z").toISOString() : null)

/** Products → Gift cards. */
export function GiftCardsPage() {
  const [params, setParams] = useSearchParams()
  const { pageIndex, q, setPage, setQ } = usePage(params, setParams)
  const status = params.get("status") ?? ""
  const { data, isLoading, error } = useGiftCards({ q, status, limit: PAGE, offset: pageIndex * PAGE })
  const can = useCan()
  const navigate = useNavigate()
  const [issuing, setIssuing] = useState(false)
  const setStatus = (v: string) => {
    const next = new URLSearchParams(params)
    v ? next.set("status", v) : next.delete("status")
    next.delete("page")
    setParams(next)
  }
  return (
    <>
      <PageHeader title="Gift cards" subtitle={data ? `${formatMoney(data.outstanding)} outstanding on active cards` : undefined} actions={can("gift_cards:write") && <Button size="small" onClick={() => setIssuing(true)}>Issue gift card</Button>} />
      <Section>
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div className="flex flex-wrap gap-1" role="tablist" aria-label="Status">
            {STATUSES.map(([v, l]) => (
              <button key={v} role="tab" aria-selected={status === v} onClick={() => setStatus(v)} className={`rounded-md px-2.5 py-1 text-[13px] ${status === v ? "bg-ui-bg-base-pressed font-semibold" : "text-ui-fg-subtle hover:bg-ui-bg-base-hover"}`}>{l}</button>
            ))}
          </div>
          <SearchInput value={q} onChange={setQ} placeholder="Search last 4 characters" />
        </div>
        {error ? (
          <ErrorState error={error} />
        ) : (
          <DataTable
            rowKey={(c) => c.id}
            rows={data?.gift_cards}
            count={data?.count ?? 0}
            pageSize={PAGE}
            pageIndex={pageIndex}
            onPageChange={setPage}
            loading={isLoading}
            onRowClick={(c) => navigate(`/products/gift-cards/${c.id}`)}
            empty={<EmptyState title={q || status ? "No gift cards match" : "No gift cards yet"} body={q || status ? undefined : "Issue a gift card to give a customer store credit. They redeem it with the code at checkout."} />}
            mobile={(c) => (
              <div className="flex flex-col gap-y-0.5">
                <div className="flex items-center justify-between"><span className="text-[13px] font-medium">{card(c)}</span><span className="text-[13px]">{formatMoney(c.balance)}</span></div>
                <div className="flex items-center justify-between text-[12px] text-ui-fg-subtle"><span className="truncate">{c.recipient_email || c.note || formatDate(c.created_at)}</span><Status value={c.status} /></div>
              </div>
            )}
            columns={[
              { key: "code", header: "Gift card", cell: (c) => <span className="text-[13px] font-medium">{card(c)}</span> },
              { key: "status", header: "Status", cell: (c) => <Status value={c.status} /> },
              { key: "to", header: "Recipient", cell: (c) => c.recipient_email ?? "" },
              { key: "exp", header: "Expires", cell: (c) => (c.expires_at ? formatDate(c.expires_at) : "Never") },
              { key: "init", header: "Initial value", cell: (c) => formatMoney(c.initial_value) },
              { key: "bal", header: "Balance", cell: (c) => formatMoney(c.balance) },
            ]}
          />
        )}
      </Section>
      {issuing && <IssueDialog onClose={() => setIssuing(false)} />}
    </>
  )
}

function IssueDialog({ onClose }: { onClose: () => void }) {
  const issue = useIssueGiftCard()
  const [amount, setAmount] = useState("")
  const [code, setCode] = useState("")
  const [email, setEmail] = useState("")
  const [expires, setExpires] = useState("")
  const [note, setNote] = useState("")
  const [created, setCreated] = useState<{ code: string; last4: string } | null>(null)
  const value = Number(amount)
  const bad = !(value >= 1 && value <= 10000) || (code !== "" && !/^[A-Za-z0-9-]{8,30}$/.test(code)) || (email !== "" && !/^\S+@\S+\.\S+$/.test(email))
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" role="dialog" aria-modal="true" aria-label="Issue gift card">
      <div className="my-8 w-full max-w-lg rounded-xl bg-ui-bg-base shadow-xl">
        <div className="border-b border-ui-border-base px-5 py-4 text-[15px] font-semibold">{created ? "Gift card created" : "Issue gift card"}</div>
        {created ? (
          <div className="flex flex-col gap-3 p-5">
            <Text size="small">Copy the code now. For security it can't be shown again; you'll only see the last four characters (•••• {created.last4}).</Text>
            <div className="rounded-lg border border-ui-border-base bg-ui-bg-subtle p-4 text-center font-mono text-[20px] tracking-wider" data-testid="gift-code">{created.code}</div>
            <Button size="small" variant="secondary" onClick={() => navigator.clipboard?.writeText(created.code).then(() => toast.success("Code copied"), () => toast.error("Couldn't copy: select the code and copy it manually"))}>Copy code</Button>
          </div>
        ) : (
          <div className="flex flex-col gap-3 p-5">
            <Field label="Initial value (USD)"><Input type="number" min={1} max={10000} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
            <Field label="Gift card code" hint="Leave blank to generate a secure code. Custom codes need 8–30 letters, numbers or dashes."><Input value={code} maxLength={30} onChange={(e) => setCode(e.target.value.trim())} /></Field>
            <Field label="Recipient email (optional)"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value.trim())} /></Field>
            <Field label="Expiry date (optional)"><Input type="date" value={expires} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setExpires(e.target.value)} /></Field>
            <Field label="Note (optional)"><Textarea rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
          </div>
        )}
        <div className="flex justify-end gap-2 border-t border-ui-border-base px-5 py-3">
          <Button variant="secondary" size="small" onClick={onClose}>{created ? "Done" : "Cancel"}</Button>
          {!created && (
            <Button size="small" disabled={bad} isLoading={issue.isPending} onClick={() => issue.mutate({ amount: value, ...(code ? { code } : {}), ...(email ? { recipient_email: email } : {}), expires_at: toIso(expires), note: note.trim() || undefined }, { onSuccess: (r) => setCreated({ code: r.code, last4: r.card.last4 }), onError: (e) => toast.error(errorMessage(e)) })}>Issue</Button>
          )}
        </div>
      </div>
    </div>
  )
}

export function GiftCardDetailPage() {
  const { id = "" } = useParams()
  const { data, error } = useGiftCard(id)
  const upd = useUpdateGiftCard(id)
  const can = useCan()
  const writable = can("gift_cards:write")
  const [adjust, setAdjust] = useState("")
  const [adjustNote, setAdjustNote] = useState("")
  const [note, setNote] = useState<string | null>(null)
  const [expires, setExpires] = useState<string | null>(null)
  if (error) return <ErrorState error={error} />
  if (!data) return null
  const c = data.gift_card
  const run = (b: Parameters<typeof upd.mutate>[0], msg: string) => upd.mutate(b, { onSuccess: () => toast.success(msg), onError: (e) => toast.error(errorMessage(e)) })
  const delta = Number(adjust)
  return (
    <div className="pb-10">
      <PageHeader back="/products/gift-cards" title={<span className="flex items-center gap-2">{card(c)} <Status value={c.status} /></span>} subtitle={`Issued ${formatDate(c.created_at)}${c.created_by ? "" : ""}`}
        actions={writable && <Button size="small" variant="secondary" isLoading={upd.isPending} onClick={() => run({ enabled: c.status === "disabled" }, c.status === "disabled" ? "Gift card enabled" : "Gift card disabled")}>{c.status === "disabled" ? "Enable" : "Disable"}</Button>} />
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="flex flex-col gap-4 lg:col-span-2">
          <Section title="Transaction history">
            <ul className="divide-y divide-ui-border-base">
              {data.transactions.map((t) => (
                <li key={t.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-[13px]">
                  <div>
                    <div className="font-semibold capitalize">{t.kind === "redeem" ? "Used at checkout" : t.kind}</div>
                    <div className="text-ui-fg-subtle">{formatDate(t.created_at)}{t.note ? ` · ${t.note}` : ""}</div>
                    {t.order_id && <Link to={`/orders/${t.order_id}`} className="text-ui-fg-interactive hover:underline">View order</Link>}
                  </div>
                  <div className="text-right"><div className={t.amount < 0 ? "text-ui-fg-error" : ""}>{t.amount ? (t.amount > 0 ? "+" : "") + formatMoney(t.amount) : "—"}</div><div className="text-[12px] text-ui-fg-subtle">Balance {formatMoney(t.balance_after)}</div></div>
                </li>
              ))}
            </ul>
          </Section>
        </div>
        <div className="flex flex-col gap-4">
          <Section title="Balance">
            <div className="flex flex-col gap-1 p-4">
              <div className="text-[24px] font-semibold">{formatMoney(c.balance)}</div>
              <Text size="small" className="text-ui-fg-subtle">of {formatMoney(c.initial_value)} initial value</Text>
              {c.expires_at && <Text size="small" className="text-ui-fg-subtle">Expires {formatDate(c.expires_at)}</Text>}
              {c.recipient_email && <Text size="small" className="text-ui-fg-subtle">For {c.recipient_email}</Text>}
            </div>
          </Section>
          {writable && (
            <>
              <Section title="Adjust balance">
                <div className="flex flex-col gap-3 p-4">
                  <Field label="Amount (use − to subtract)"><Input type="number" step="0.01" value={adjust} onChange={(e) => setAdjust(e.target.value)} /></Field>
                  <Field label="Reason"><Input value={adjustNote} maxLength={200} onChange={(e) => setAdjustNote(e.target.value)} /></Field>
                  <Button size="small" disabled={!delta || Math.abs(delta) > 10000} isLoading={upd.isPending} onClick={() => run({ adjust: delta, adjust_note: adjustNote.trim() || undefined }, "Balance updated")}>Apply adjustment</Button>
                </div>
              </Section>
              <Section title="Details">
                <div className="flex flex-col gap-3 p-4">
                  <Field label="Expiry date"><Input type="date" value={expires ?? dateOnly(c.expires_at)} onChange={(e) => setExpires(e.target.value)} /></Field>
                  <Field label="Note"><Textarea rows={2} maxLength={500} value={note ?? c.note} onChange={(e) => setNote(e.target.value)} /></Field>
                  <Button size="small" variant="secondary" disabled={note === null && expires === null} isLoading={upd.isPending} onClick={() => run({ ...(note !== null ? { note } : {}), ...(expires !== null ? { expires_at: toIso(expires) } : {}) }, "Details saved")}>Save details</Button>
                </div>
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
